import type { Editor } from '@tiptap/react';
import { useChat } from '@ai-sdk/react';
import { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';

import { useAccessTokenGetter } from '@flintmint/web-shared/hooks/use-auth-user';
import { useStreamingEnabled } from '@/hooks/use-streaming-enabled';
import { useLLMContext } from '@/hooks/llm/use-llm-context/use-llm-context';
import { useClassificationCache } from './use-classification-cache';
import { autoNameDocument, type AutoNameDocumentParams } from './auto-name-document';

import { getAgentSession, setAgentAccessTokenGetter } from './session/agent-session';
import type { GenerateTextSessionParams } from './session/agent-generate-text';
import { useAgentRunUiState } from './run/agent-run-store';
import { STREAMING_UI_FRAME_MS } from './streaming/streaming-preview';
import type { AgentEditTarget } from './edit-target';
import type { AgentDocumentType, PersistedDocument } from './document-utils';
import type { PersistedDocumentWriter } from './review/review-session-types';

export type AgentFinishResult = {
  hasEdit: boolean;
  requestId: string;
  documentName?: string;
  documentType?: AgentDocumentType;
};

type LLMAgentOptions = {
  editor: Editor | null;
  requestId: string;
  document?: PersistedDocument;
  persistDocumentPatch?: PersistedDocumentWriter;
  onEditPhaseFinish?: (result?: AgentFinishResult) => void;
  onFinish?: (result?: AgentFinishResult) => void;
  onError?: (error: Error) => void;
};

export const useLLMAgent = ({
  editor,
  onError,
  document,
  onFinish,
  requestId,
  onEditPhaseFinish,
  persistDocumentPatch,
}: LLMAgentOptions) => {
  const getAccessToken = useAccessTokenGetter();
  const streamingEnabled = useStreamingEnabled();
  const { isLoading: isActive, isNetworkError, currentStep } = useAgentRunUiState(requestId);
  const { getContext, getGenerateTextContext } = useLLMContext();
  const session = useMemo(() => getAgentSession(requestId), [requestId]);

  const {
    messages,
    addToolOutput,
    error: chatError,
  } = useChat({
    chat: session.chat,
    experimental_throttle: STREAMING_UI_FRAME_MS, // ~30fps, shared with editor preview coalescing
  });
  const chatMessages = session.updateChatTranscript(messages);

  const { handleSuggestionDecision, reset: resetClassificationCache } = useClassificationCache({
    messages,
    requestId,
    document,
  });

  const autoNameIfNeeded = useCallback(
    (params: AutoNameDocumentParams) =>
      autoNameDocument({
        ...params,
        generateDocumentName: session.generateDocumentName,
        getContext,
      }),
    [session, getContext]
  );

  useLayoutEffect(() => {
    setAgentAccessTokenGetter(getAccessToken);
  }, [getAccessToken]);

  useEffect(() => {
    session.markMounted();
    return () => session.markUnmounted();
  }, [session]);

  useLayoutEffect(() => {
    session.setRuntime({
      editor,
      addToolOutput,
      document,
      persistDocumentPatch,
      getContext,
      getGenerateTextContext,
      autoNameIfNeeded,
      resetClassificationCache,
      onEditPhaseFinish,
      onFinish,
      onError,
      streamingEnabled,
    });
  }, [
    session,
    editor,
    addToolOutput,
    document,
    persistDocumentPatch,
    getContext,
    getGenerateTextContext,
    autoNameIfNeeded,
    resetClassificationCache,
    onEditPhaseFinish,
    onFinish,
    onError,
    streamingEnabled,
  ]);

  const sendChatMessage = useCallback(
    (text: string, options?: { editTarget?: AgentEditTarget }) => session.send(text, options),
    [session]
  );

  const generateText = useCallback((params: GenerateTextSessionParams) => session.generateText(params), [session]);

  const cancel = useCallback(() => session.cancel(), [session]);

  const retryLastMessage = useCallback(() => session.retry(), [session]);

  return {
    cancel,
    isActive,
    messages,
    chatMessages,
    currentStep,
    sendChatMessage,
    generateText,
    handleSuggestionDecision,
    error: chatError,
    isNetworkError,
    retryLastMessage,
  };
};
